import { Crud } from "@/api";
import { timed } from "@/utils/loaderLogger";
import { getRequestPagination } from "@/components/Pagination/Pagination";
import {
  Order,
  ShippingMethod,
  ShippingStatus,
  WorkListGroup,
  WorkListStatus,
  WorklistColumn,
} from "@/utils/types/types";
import { LoaderFunctionArgs, redirect } from "react-router-dom";

export type OrdersData = {
  orders: Order[];
  pagination: ReturnType<typeof getRequestPagination>["pagination"];
  worklistGroups: WorkListGroup[];
  worklistStatuses: WorkListStatus[];
  worklistColumns: WorklistColumn[];
  shippingMethods: ShippingMethod[];
  shippingStatuses: ShippingStatus[];
};

export default function dashboardOrdersLoader({ request }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url);
  const { orderNo } = Object.fromEntries(searchParams.entries());
  // A company name is only ever in the email domain ("Buck Mason" → carsen@buckmason.com) and a
  // two-word name only matches the order name by its last word ("T-50604_Berry") — 09-22, Michael.
  const compact = orderNo ? orderNo.replace(/\s+/g, "") : "";
  const lastWord = orderNo ? orderNo.trim().split(/\s+/).slice(-1)[0] : "";
  const hasSpace = !!orderNo && compact !== orderNo.trim();

  const { paginationUrl, pagination } = getRequestPagination({
    request,
    pageSize: 100,
  });
  if (paginationUrl) throw redirect(paginationUrl);

  const data: Promise<OrdersData> = Promise.all([
    timed(
      "orders.getAll",
      Crud.orders.getAll(
        orderNo
          ? ({
              OR: [
                { orderNo: { contains: orderNo } },
                { displayOrderNo: { contains: orderNo } },
                { customerEmail: { contains: orderNo, mode: "insensitive" } },
                { orderName: { contains: orderNo, mode: "insensitive" } },
                ...(hasSpace
                  ? [
                      { customerEmail: { contains: compact, mode: "insensitive" } },
                      { orderName: { contains: lastWord, mode: "insensitive" } },
                    ]
                  : []),
              ],
              pagination,
            } as any)
          : ({ pagination } as any)
      )
    ),
    timed("worklistGroups.getAll", Crud.worklistGroups.getAll()),
    timed("worklistStatuses.getAll", Crud.worklistStatuses.getAll()),
    timed("worklistColumns.getAll", Crud.worklistColumns.getAll()),
    timed("shippingMethods.getAll", Crud.shippingMethods.getAll()),
    timed("shippingStatuses.getAll", Crud.shippingStatuses.getAll()),
  ]).then(
    ([
      orders,
      worklistGroups,
      worklistStatuses,
      worklistColumns,
      shippingMethods,
      shippingStatuses,
    ]) => ({
      orders,
      pagination,
      worklistGroups,
      worklistStatuses,
      worklistColumns,
      shippingMethods,
      shippingStatuses,
    })
  );

  return { data };
}
